/**
 * content — client feature module (spec §4.1, brief §content): topic
 * browser, formula reference (with the print stylesheet) and the past-exam
 * archive.
 *
 * One top-level path (`/library`) with three children (`topics`, `formulas`,
 * `exams`) — not three top-level routes — so this feature still contributes
 * exactly one entry to `client/src/app/router.test.tsx`'s "matches every
 * route every feature registers" check (frozen: one path per feature), the
 * same pattern `practice/index.ts`, `exam/index.ts` and `flashcards/index.ts`
 * use. `/library` itself has no page: the index child redirects to
 * `/library/topics` (`Page.tsx`). None of the three children is a prefix of
 * another, so each nav entry can use `end: true` and highlight correctly
 * without needing a "hub" page.
 *
 * Topics and the archive are `hideDuringExam` (spec §4.5): not available
 * while an exam session is running. The formula sheet stays (ruling 6, slice
 * D): it is an allowed aid on the real exam, so the övningstenta has it too.
 *
 * They also share `groupKey: "content.nav.library"`, so the shell shows them
 * as one "Bibliotek / Library" menu and the home page as one card.
 */
import { Archive, BookOpen, Sigma } from "lucide-react";
import type { FeatureModule } from "@client/app/types";
import { ExamsPage } from "./ExamsPage";
import { FormulasPage } from "./FormulasPage";
import { LibraryExamGuard } from "./ExamGuard";
import { ArchiveRedirect, ContentPage } from "./Page";
import { TopicsPage } from "./TopicsPage";

const contentFeature: FeatureModule = {
  id: "content",
  routes: [
    {
      path: "/library",
      // Slice D fix I1: during an övningstenta only the formula sheet opens.
      Component: LibraryExamGuard,
      children: [
        { index: true, Component: ContentPage },
        { path: "topics", Component: TopicsPage },
        { path: "formulas", Component: FormulasPage },
        { path: "exams", Component: ExamsPage },
        { path: "archive", Component: ArchiveRedirect },
      ],
    },
  ],
  nav: [
    {
      to: "/library/topics",
      labelKey: "content.nav.topics",
      groupKey: "content.nav.library",
      icon: BookOpen,
      order: 50,
      hideDuringExam: true,
      end: true,
    },
    {
      to: "/library/formulas",
      labelKey: "content.nav.formulas",
      groupKey: "content.nav.library",
      icon: Sigma,
      order: 51,
      end: true,
    },
    {
      to: "/library/exams",
      labelKey: "content.nav.exams",
      groupKey: "content.nav.library",
      icon: Archive,
      order: 52,
      hideDuringExam: true,
      end: true,
    },
  ],
  dict: {
    sv: {
      "content.nav.library": "Bibliotek",
      "content.examLocked.title": "Inte tillgängligt under övningstentan",
      "content.examLocked.body":
        "Medan du skriver övningstentan är bara formelsamlingen öppen, precis som på riktig tenta.",
      "content.examLocked.formulas": "Till formelsamlingen",
      "content.examLocked.back": "Tillbaka till tentan",
      "content.nav.topics": "Ämnen",
      "content.nav.formulas": "Formler",
      "content.nav.exams": "Arkiv",

      "content.topics.description":
        "Momentöversikt med alla ämnen — hur mycket övningsmaterial som finns och hur tungt varje ämne väger på tentan.",
      "content.topics.book": "Y&F {sections}",
      "content.book.lectureNotes": "föreläsningsanteckningar",
      "content.topics.count.one": "{count} ämne",
      "content.topics.count.other": "{count} ämnen",
      "content.topics.legend":
        "Y&F = Young & Freedman, University Physics, 15:e uppl. Kursbok: kapitel 37–44. Momentnumren är kursens indelning; materialbanken täcker ännu inte hela bokintervallet.",
      "content.topics.count": "{count} ämnen",
      "content.topics.cursory": "Kursiv läsning",
      "content.topics.weight": "Tentavikt (ungefärlig): {level}",
      "content.topics.weight.low": "låg",
      "content.topics.weight.mid": "medel",
      "content.topics.weight.high": "hög",
      "content.topics.weight.hint":
        "Ungefärlig uppskattning av hur mycket ämnet brukar väga på tentan.",
      "content.topics.lectureNotes": "Föreläsningsanteckningar",
      "content.topics.counts.questions": "{count} frågor",
      "content.topics.counts.flashcards": "{count} kort",
      "content.topics.counts.empty": "Inget övningsmaterial ännu",
      "content.topics.type.mc": "flerval",
      "content.topics.type.numeric": "räkna",
      "content.topics.type.open": "uppgift",
      "content.topics.practice": "Öva detta",
      "content.topics.empty": "Inga moment hittades.",

      "content.lecture.card.label": "Föreläsningar",
      "content.lecture.card.next": "Nästa: {id} · {date}",
      "content.lecture.card.today": "Idag: {id}",
      "content.lecture.card.starts": "Kursen börjar {date} med {id}",
      "content.lecture.card.allDone": "Alla föreläsningar är genomgångna",
      "content.lecture.card.exam": "Skriv en övningstenta",
      "content.lecture.card.all": "Alla föreläsningar",
      "content.lecture.reading": "Läs: Y&F {sections}",
      "content.lecture.prepare": "Förbered {id}",
      "content.lecture.review": "Repetera {id}",
      "content.lecture.practise": "Öva",
      "content.lecture.practiseLabel": "Öva {id}",
      "content.lecture.unavailable": "Inga frågor ännu",
      "content.lecture.opens": "Öppnas {date}",
      "content.lecture.closed": "Stängd tills vidare",
      "content.lecture.state.done": "Genomgången",
      "content.lecture.state.today": "Idag",
      "content.lecture.state.next": "Nästa",
      "content.lecture.state.upcoming": "Kommande",
      "content.lecture.topics": "Ämnen: {topics}",
      "content.topics.view.label": "Visa ämnen",
      "content.topics.view.modules": "Moment",
      "content.topics.view.lectures": "Föreläsning",
      "content.topics.lectures.intro":
        "Kursens tillfällen F01–F12 och R01–R03 i datumordning. Öva öppnar tillfällets frågor; du trycker själv på Starta.",
      "content.topics.lectures.empty": "Föreläsningsschemat kunde inte läsas.",

      "content.formulas.description":
        "Formler grupperade per moment — samma formler som i formelsamlingen från kursen.",
      "content.formulas.chapterTab": "Moment {number}",
      "content.formulas.variables": "Variabler",
      "content.formulas.variableSymbol": "Storhet",
      "content.formulas.variableMeaning": "Betydelse",
      "content.formulas.print": "Skriv ut",
      "content.formulas.empty": "Inga formler i det här momentet ännu.",

      "content.exams.title": "Arkiv",
      "content.exams.description":
        "Gamla tentor, övningstentor och hemuppgifter i sin helhet, med lösningar.",
      "content.exams.contents.title": "Innehåll",
      "content.exams.contents.backToTop": "Till början",
      "content.exams.pastExams": "Gamla tentor",
      "content.exams.practicePapers": "Övningstentor",
      "content.exams.practicePapers.note":
        "Nyskrivna övningstentor från kursens uppgiftsbank — inte officiella tentor, men samma format: 7 uppgifter, 18 poäng.",
      "content.exams.practicePapers.empty": "Inga övningstentor är granskade ännu.",
      "content.exams.homework.title": "Hemuppgifter",
      "content.exams.homework.heading": "Hemuppgifter {term}",
      "content.exams.homework.assignment": "Hemuppgift {number}",
      "content.exams.homework.empty": "Inga hemuppgifter ännu.",
      "content.exams.problemTitle": "Uppgift {number}",
      "content.exams.reveal": "Visa lösning",
      "content.exams.hide": "Dölj lösning",
      "content.exams.rubric": "Bedömningspunkter",
      "content.exams.finalAnswer": "Svar",
      "content.exams.practiceSimilar": "Öva liknande",
      "content.exams.sitThis": "Skriv som övningstenta",
      "content.exams.empty": "Inga gamla tentor ännu.",
      "content.exams.dateUnknown": "Datum okänt",
    },
    en: {
      "content.nav.library": "Library",
      "content.examLocked.title": "Not available during the practice exam",
      "content.examLocked.body":
        "While you write the practice exam only the formula sheet is open, as in the real exam.",
      "content.examLocked.formulas": "To the formula sheet",
      "content.examLocked.back": "Back to the exam",
      "content.nav.topics": "Topics",
      "content.nav.formulas": "Formulas",
      "content.nav.exams": "Archive",

      "content.topics.description":
        "Module overview with every topic — how much practice material exists and how heavily each topic weighs on the exam.",
      "content.topics.book": "Y&F {sections}",
      "content.book.lectureNotes": "lecture notes",
      "content.topics.count.one": "{count} topic",
      "content.topics.count.other": "{count} topics",
      "content.topics.legend":
        "Y&F = Young & Freedman, University Physics, 15th ed. Course book: chapters 37–44. Module numbers belong to the course; the bank does not yet cover the full book range.",
      "content.topics.count": "{count} topics",
      "content.topics.weight": "Exam weight (approximate): {level}",
      "content.topics.weight.low": "low",
      "content.topics.weight.mid": "medium",
      "content.topics.weight.high": "high",
      "content.topics.weight.hint":
        "A rough estimate of how much the topic usually counts on the exam.",
      "content.topics.cursory": "Cursory reading",
      "content.topics.lectureNotes": "Lecture notes",
      "content.topics.counts.questions": "{count} questions",
      "content.topics.counts.flashcards": "{count} cards",
      "content.topics.counts.empty": "No practice material yet",
      "content.topics.type.mc": "multiple choice",
      "content.topics.type.numeric": "numeric",
      "content.topics.type.open": "problem",
      "content.topics.practice": "Practice this",
      "content.topics.empty": "No modules found.",

      "content.lecture.card.label": "Lectures",
      "content.lecture.card.next": "Next: {id} · {date}",
      "content.lecture.card.today": "Today: {id}",
      "content.lecture.card.starts": "The course starts {date} with {id}",
      "content.lecture.card.allDone": "All lectures have been held",
      "content.lecture.card.exam": "Sit a practice exam",
      "content.lecture.card.all": "All lectures",
      "content.lecture.reading": "Read: Y&F {sections}",
      "content.lecture.prepare": "Prepare {id}",
      "content.lecture.review": "Review {id}",
      "content.lecture.practise": "Practise",
      "content.lecture.practiseLabel": "Practise {id}",
      "content.lecture.unavailable": "No questions yet",
      "content.lecture.opens": "Opens {date}",
      "content.lecture.closed": "Closed until further notice",
      "content.lecture.state.done": "Held",
      "content.lecture.state.today": "Today",
      "content.lecture.state.next": "Next",
      "content.lecture.state.upcoming": "Upcoming",
      "content.lecture.topics": "Topics: {topics}",
      "content.topics.view.label": "Show topics",
      "content.topics.view.modules": "Module",
      "content.topics.view.lectures": "Lecture",
      "content.topics.lectures.intro":
        "The course's sessions F01–F12 and R01–R03 in date order. Practise opens the session's questions; you press Start yourself.",
      "content.topics.lectures.empty": "The lecture schedule could not be read.",

      "content.formulas.description":
        "Formulas grouped by module — the same formulas as the course's formula sheet.",
      "content.formulas.chapterTab": "Module {number}",
      "content.formulas.variables": "Variables",
      "content.formulas.variableSymbol": "Quantity",
      "content.formulas.variableMeaning": "Meaning",
      "content.formulas.print": "Print",
      "content.formulas.empty": "No formulas in this module yet.",

      "content.exams.title": "Archive",
      "content.exams.description":
        "Past exams, practice papers and hand-ins in full, with solutions.",
      "content.exams.contents.title": "Contents",
      "content.exams.contents.backToTop": "Back to top",
      "content.exams.pastExams": "Past exams",
      "content.exams.practicePapers": "Practice papers",
      "content.exams.practicePapers.note":
        "Newly written practice papers from the course question bank — not official exams, but the same format: 7 problems, 18 points.",
      "content.exams.practicePapers.empty": "No practice papers have been checked yet.",
      "content.exams.homework.title": "Hand-ins",
      "content.exams.homework.heading": "Hand-ins {term}",
      "content.exams.homework.assignment": "Hand-in {number}",
      "content.exams.homework.empty": "No hand-ins yet.",
      "content.exams.problemTitle": "Problem {number}",
      "content.exams.reveal": "Show solution",
      "content.exams.hide": "Hide solution",
      "content.exams.rubric": "Rubric",
      "content.exams.finalAnswer": "Answer",
      "content.exams.practiceSimilar": "Practice similar",
      "content.exams.sitThis": "Sit as a practice exam",
      "content.exams.empty": "No past exams yet.",
      "content.exams.dateUnknown": "Date unknown",
    },
  },
};

export default contentFeature;
